import { useEffect, useMemo, useState } from "react";

const fallbackDurableCustodyStatus = {
  version: "strategy-durable-custody-0.1.0",
  stores: 0,
  tables: 0,
  apiRoutes: 0,
  blockedGates: 1,
  serverCustodyClaim: false,
  claimBoundary:
    "Runtime receipt status has not been loaded. No server custody claim is made by the UI fallback.",
  smokeProof: null
};

const fallbackDeploymentStatus = {
  localReady: false,
  productionDeployClaim: false,
  liveUrlVerified: false,
  cloudflareAuthVerified: false,
  rollbackVerified: false,
  requiredLiveProofs: 0,
  missingProofs: 1,
  manualBlockers: 1,
  warningBlockers: 0,
  deployMode: null,
  liveUrl: null,
  claimBoundary:
    "Runtime receipt status has not been loaded. No live deployment or production availability is claimed by the UI fallback.",
  panelBoundary: "No live deployment or production availability is claimed by the UI fallback."
};

function countBlockers(blockers = [], status) {
  return blockers.filter((blocker) => blocker.status === status).length;
}

function deploymentFromReleaseReceipt(releaseReceipt) {
  const readiness = releaseReceipt?.deploymentReadiness ?? {};
  const liveProof = readiness.liveProofEvidence ?? {};
  const liveUrlVerified = Boolean(readiness.liveUrlVerified);
  const productionDeployClaim = Boolean(readiness.productionDeployClaim);

  return {
    localReady: Boolean(readiness.localReady),
    productionDeployClaim,
    liveUrlVerified,
    cloudflareAuthVerified: Boolean(readiness.cloudflareAuthVerified),
    rollbackVerified: Boolean(readiness.rollbackVerified),
    requiredLiveProofs: readiness.requiredLiveProofs?.length ?? 0,
    missingProofs: readiness.blockers?.filter((blocker) => blocker.status === "manual_required").length ?? 0,
    manualBlockers: countBlockers(readiness.blockers, "manual_required"),
    warningBlockers: countBlockers(readiness.blockers, "warning"),
    deployMode: liveProof.deployMode ?? null,
    liveUrl: liveProof.liveUrl ?? null,
    claimBoundary: readiness.claimBoundary ?? fallbackDeploymentStatus.claimBoundary,
    panelBoundary: productionDeployClaim
      ? "Production deployment proof is present. Professional advice, corpus-completion, and formal-verification claims remain blocked unless separately proven."
      : liveUrlVerified
        ? "Live technical preview is verified. Production deployment remains blocked until production-mode proof is supplied."
        : "Local build/config readiness is recorded. No live deployment or production availability is claimed."
  };
}

function durableCustodyFromReleaseReceipt(releaseReceipt) {
  const durable = releaseReceipt?.durableCustody ?? {};
  const summary = durable.summary ?? {};

  return {
    version: summary.version ?? durable.version ?? durable.schemaVersion ?? fallbackDurableCustodyStatus.version,
    stores: summary.stores ?? durable.stores?.length ?? 0,
    tables: summary.tables ?? durable.tables?.length ?? 0,
    apiRoutes: summary.apiRoutes ?? durable.apiRoutes?.length ?? 0,
    blockedGates: summary.blockedGates ?? 0,
    serverCustodyClaim: Boolean(summary.serverCustodyClaim),
    claimBoundary: summary.claimBoundary ?? durable.claimBoundary ?? fallbackDurableCustodyStatus.claimBoundary,
    smokeProof: durable.smokeProof ?? null
  };
}

export function currentDeploymentStatus(releaseReceipt) {
  if (!releaseReceipt) return fallbackDeploymentStatus;
  return deploymentFromReleaseReceipt(releaseReceipt);
}

export function currentDurableCustodyStatus(releaseReceipt) {
  if (!releaseReceipt) return fallbackDurableCustodyStatus;
  return durableCustodyFromReleaseReceipt(releaseReceipt);
}

export function useRuntimeProofStatus() {
  const [releaseReceipt, setReleaseReceipt] = useState(undefined);

  useEffect(() => {
    let active = true;
    fetch("/strategy-release-receipt.json", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((receipt) => {
        if (active && receipt) setReleaseReceipt(receipt);
      })
      .catch(() => {
        if (active) setReleaseReceipt(null);
      });

    return () => {
      active = false;
    };
  }, []);

  return useMemo(
    () => ({
      durable: currentDurableCustodyStatus(releaseReceipt),
      deployment: currentDeploymentStatus(releaseReceipt),
      loaded: releaseReceipt !== undefined,
      sourceAvailable: Boolean(releaseReceipt)
    }),
    [releaseReceipt]
  );
}
